import { type ReactNode } from 'react';
import {
  AlertCircle,
  CircleOff,
  Inbox,
  Lock,
  Monitor,
  SearchX,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils.ts';

/**
 * EmptyState — single primitive for every "nothing to show" surface.
 * Spec §9.6. Variants make the visual treatment consistent so users
 * learn to recognize the pattern across the product.
 *
 * Variants:
 *   no-data           — list is empty (e.g. "No incidents in the last 7d")
 *   no-results        — filter / search returned zero matches
 *   desktop-only      — page or feature is DO per spec §4
 *   permission-denied — user lacks the right permission
 *   error             — request failed; usually pairs with a Retry action
 *   not-found         — 404 / unknown resource
 *
 * Sizing: `inline` for content slots, `page` (default) for whole-page
 * empty states. Both center their content; `page` adds vertical padding
 * so the state doesn't hug the top of the page.
 */

export type EmptyStateVariant =
  | 'no-data'
  | 'no-results'
  | 'desktop-only'
  | 'permission-denied'
  | 'error'
  | 'not-found';

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  /** Heading. Falls back to the variant's default if omitted. */
  title?: ReactNode;
  /** Optional body copy. */
  description?: ReactNode;
  /** Optional action — usually a <Button>. */
  action?: ReactNode;
  /** Optional secondary action; rendered next to the primary. */
  secondaryAction?: ReactNode;
  /** Override the variant's default icon. */
  icon?: LucideIcon;
  /** `inline` is centered without extra vertical padding. Default `page`. */
  size?: 'page' | 'inline';
  className?: string;
}

interface VariantConfig {
  icon: LucideIcon;
  defaultTitle: string;
  iconClassName: string;
}

const VARIANTS: Record<EmptyStateVariant, VariantConfig> = {
  'no-data': {
    icon: Inbox,
    defaultTitle: 'Nothing here yet',
    iconClassName: 'text-muted-foreground',
  },
  'no-results': {
    icon: SearchX,
    defaultTitle: 'No matches',
    iconClassName: 'text-muted-foreground',
  },
  'desktop-only': {
    icon: Monitor,
    defaultTitle: 'Desktop-only',
    iconClassName: 'text-muted-foreground',
  },
  'permission-denied': {
    icon: Lock,
    defaultTitle: 'Not authorized',
    iconClassName: 'text-muted-foreground',
  },
  error: {
    icon: AlertCircle,
    defaultTitle: 'Something went wrong',
    iconClassName: 'text-destructive',
  },
  'not-found': {
    icon: CircleOff,
    defaultTitle: 'Not found',
    iconClassName: 'text-muted-foreground',
  },
};

export function EmptyState({
  variant = 'no-data',
  title,
  description,
  action,
  secondaryAction,
  icon,
  size = 'page',
  className,
}: EmptyStateProps) {
  const config = VARIANTS[variant];
  const Icon = icon ?? config.icon;
  const resolvedTitle = title ?? config.defaultTitle;

  return (
    <div
      className={cn(
        'flex flex-col items-center text-center max-w-md mx-auto',
        size === 'page' ? 'py-12 md:py-16 px-4' : 'py-6 px-4',
        className,
      )}
      // Variants that gate functionality (DO, permission-denied) should
      // be readable to AT users as informative regions, not as silent
      // empty containers.
      role={variant === 'error' ? 'alert' : 'status'}
    >
      <div
        className={cn(
          'mb-4 inline-flex items-center justify-center rounded-full bg-muted',
          size === 'page' ? 'w-14 h-14' : 'w-10 h-10',
        )}
      >
        <Icon
          aria-hidden="true"
          className={cn(size === 'page' ? 'w-6 h-6' : 'w-5 h-5', config.iconClassName)}
        />
      </div>

      <h2
        className={cn(
          'font-semibold tracking-tight',
          size === 'page' ? 'text-lg md:text-xl' : 'text-base',
        )}
      >
        {resolvedTitle}
      </h2>

      {description ? (
        <p
          className={cn(
            'text-muted-foreground mt-1.5',
            size === 'page' ? 'text-sm md:text-base' : 'text-sm',
          )}
        >
          {description}
        </p>
      ) : null}

      {action ?? secondaryAction ? (
        <div className="mt-5 flex items-center gap-2 flex-wrap justify-center">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
